import fetchClient from '@eureca/recruiter-api-sdk/client';
import { recruiterEnv } from '@/config/env';
import { getKeycloak } from '@/auth/keycloak-instance';
import { triggerDownload } from '@/shared/xlsx/download-xlsx';
import { JobImportApiError } from './job-import-errors';

/**
 * DEV-276 — chamadas do caminho "Em massa". Upload e downloads são BINÁRIOS
 * (fetch cru com o bearer do Keycloak, molde `download-program-base.ts`); a
 * confirmação passa pelo SDK (JSON).
 */
export interface ImportSummary {
  toCreate: number;
  toUpdate: number;
  invalid: number;
  published: number;
}

export interface ConflictRow {
  rowNumber: number;
  externalId: string;
  name: string;
  classification: 'Vaga inválida' | 'Vaga já publicada';
  reason: string;
}

export interface PreparedImport {
  batchId: string;
  programName: string;
  summary: ImportSummary;
  conflicts: ConflictRow[];
}

export interface ImportResult {
  created: number;
  updated: number;
  alreadyConfirmed: boolean;
  createdJobIds: string[];
  updatedJobIds: string[];
}

/** Teto do CLIENTE para o envio (20 MB numa conexão lenta); abaixo do BFF→Core (280 s). */
export const UPLOAD_TIMEOUT_MS = 180_000;
export const IMPORT_TEMPLATE_FILE_NAME = 'Modelo de criação de vagas em massa.xlsx';

async function bearer(): Promise<string | null> {
  const keycloak = getKeycloak();
  try {
    await keycloak.updateToken(30);
  } catch {
    // expirado → 401 claro do BFF
  }
  return keycloak.token ?? null;
}

async function errorFrom(res: Response): Promise<JobImportApiError> {
  const body: unknown = await res.json().catch(() => null);
  const code =
    body && typeof body === 'object' && 'error' in body
      ? String((body as { error: unknown }).error)
      : null;
  return new JobImportApiError(res.status, code, body);
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

/**
 * Envia a planilha. `signal` é o cancelamento do chamador (Voltar, troca de
 * formato, saída da tela): quem abortar primeiro — ele ou o teto de tempo —
 * encerra o request. Cancelamento rejeita com `AbortError`; teto, com
 * `TimeoutError` (cada um tem a sua mensagem em `uploadErrorMessage`).
 */
export async function uploadJobImport(
  programId: string,
  file: File,
  signal?: AbortSignal
): Promise<PreparedImport> {
  const token = await bearer();
  const form = new FormData();
  form.append('file', file, file.name);
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(signal?.reason);
  if (signal?.aborted) abortFromCaller();
  else signal?.addEventListener('abort', abortFromCaller, { once: true });
  const timer = setTimeout(
    () => controller.abort(new DOMException('Upload timed out', 'TimeoutError')),
    UPLOAD_TIMEOUT_MS
  );
  try {
    const res = await fetch(new URL(`${recruiterEnv.apiUrl}/programs/${programId}/job-imports`), {
      method: 'POST',
      headers: token ? { authorization: `Bearer ${token}` } : {},
      body: form,
      signal: controller.signal,
    });
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as PreparedImport;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

export async function confirmJobImport(batchId: string): Promise<ImportResult> {
  const res = await fetchClient<ImportResult>({
    method: 'POST',
    url: `/job-imports/${batchId}/confirm`,
  });
  if (res.error) {
    const err = res.error as { status: number; error?: unknown };
    const body = err.error;
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : null;
    throw new JobImportApiError(err.status, code, body);
  }
  return res.data;
}

async function downloadBinary(path: string, fallbackName: string): Promise<string> {
  const token = await bearer();
  const res = await fetch(new URL(`${recruiterEnv.apiUrl}${path}`), {
    method: 'GET',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
  });
  if (!res.ok) throw await errorFrom(res);
  const blob = await res.blob();
  const fileName =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallbackName;
  triggerDownload(blob, fileName);
  return fileName;
}

export function downloadImportTemplate(): Promise<string> {
  return downloadBinary('/job-imports/template', IMPORT_TEMPLATE_FILE_NAME);
}

export function downloadImportConflicts(batchId: string): Promise<string> {
  return downloadBinary(`/job-imports/${batchId}/conflicts`, 'conflitos.xlsx');
}
